import { useEffect, useRef } from "react";

export interface UseAutoRefreshOptions {
  enabled: boolean;
  intervalMs: number;
  onRefresh: () => void;
  refreshWhenHidden?: boolean;
}

/**
 * Polling simples para telas operacionais.
 *
 * Por padrao, nao atualiza quando a aba esta oculta. Isso evita chamadas
 * desnecessarias enquanto o operador nao esta olhando para a dashboard.
 */
export function useAutoRefresh({
  enabled,
  intervalMs,
  onRefresh,
  refreshWhenHidden = false,
}: UseAutoRefreshOptions): void {
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;

    const timer = window.setInterval(() => {
      if (
        !refreshWhenHidden &&
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return;
      }

      refreshRef.current();
    }, intervalMs);

    // Em abas ocultas o navegador ESTRANGULA o setInterval (até ~1/min) e
    // pulamos os ticks. Sem isso, ao voltar à aba o card fica "sem leitura
    // recente" até o próximo tick lento. Aqui forçamos um refresh IMEDIATO
    // assim que a aba volta a ficar visível — recuperação instantânea.
    let onVisible: (() => void) | undefined;
    if (!refreshWhenHidden && typeof document !== "undefined") {
      onVisible = () => {
        if (document.visibilityState === "visible") refreshRef.current();
      };
      document.addEventListener("visibilitychange", onVisible);
    }

    return () => {
      window.clearInterval(timer);
      if (onVisible) document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, intervalMs, refreshWhenHidden]);
}
